import React, { useState, useImperativeHandle, forwardRef, useRef, useEffect } from 'react';
import { useDataContext } from '../../context/data.js';
import { geocodeAddress } from '../../utils/geocode.js';
import { fetchAddressSuggestions, isDawaSupported, MIN_QUERY_LENGTH } from '../../utils/dawa.js';
import translations from '../../constants/translations.json';

// Long enough that a fast typist doesn't fire a request per keystroke, short
// enough that the list feels live.
const SUGGEST_DEBOUNCE_MS = 200;

// The three fields the lookup — and the Wolt quote — both need.
const lookupKey = (address) =>
  [address?.street, address?.zipcode, address?.city].map((v) => (v || '').trim()).join('|');

const isComplete = (address) =>
  !!(address?.street?.trim() && address?.zipcode?.trim() && address?.city?.trim());

const AddressInformation = forwardRef(
  (
    {
      address,
      setAddress,
      onCheckWoltDeliveryBlur,
      // Drops the quote held for the address as it was. Called on every edit,
      // so Next can never go ahead on the previous address's quote while the
      // new one is still being typed.
      onAddressEdit,
      deliveryComment,
      onDeliveryCommentChange,
      restaurant,
    },
    ref,
  ) => {
    const { language } = useDataContext();

    // Form validation states
    const [streetError, setStreetError] = useState(false);
    const [cityError, setCityError] = useState(false);
    const [zipcodeError, setZipcodeError] = useState(false);
    const [formTouched, setFormTouched] = useState(false);

    // Address lookup state: null | 'looking' | 'resolved' | 'not_found' | 'unavailable'
    const [lookupStatus, setLookupStatus] = useState(null);
    // only the newest lookup may write to state — earlier ones can resolve late
    const requestSeqRef = useRef(0);
    // the address text the last lookup ran on, so re-blurring doesn't re-ask
    const lastLookupRef = useRef(null);
    // set when a lookup has just rewritten the address and the quote owes it a re-run
    const pendingQuoteRef = useRef(false);
    const isMountedRef = useRef(true);

    // Suggestion list state — Danish venues only, where the register has data
    const [suggestions, setSuggestions] = useState([]);
    const [highlighted, setHighlighted] = useState(-1);
    const streetInputRef = useRef(null);
    const suggestTimerRef = useRef(null);
    const suggestAbortRef = useRef(null);

    const suggestEnabled = isDawaSupported(restaurant);

    useEffect(() => {
      return () => {
        isMountedRef.current = false;
        requestSeqRef.current += 1;
        clearTimeout(suggestTimerRef.current);
        suggestAbortRef.current?.abort();
      };
    }, []);

    // The quote reads the address from its own closure, so it has to run after
    // React has flushed the one the lookup just wrote — not in the same tick.
    useEffect(() => {
      if (!pendingQuoteRef.current) return;
      pendingQuoteRef.current = false;
      onCheckWoltDeliveryBlur();
      // onCheckWoltDeliveryBlur is re-created every parent render; keying the
      // effect on it would re-quote on every render instead of every lookup.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [address]);

    const closeSuggestions = () => {
      clearTimeout(suggestTimerRef.current);
      suggestAbortRef.current?.abort();
      setSuggestions([]);
      setHighlighted(-1);
    };

    // Suggestions come from the Danish address register as the customer types.
    // They only name an address — the geocode lookup below is what the order is
    // actually placed with.
    const runSuggest = (value, caretpos) => {
      clearTimeout(suggestTimerRef.current);
      suggestAbortRef.current?.abort();

      if (!suggestEnabled || (value || '').trim().length < MIN_QUERY_LENGTH) {
        setSuggestions([]);
        setHighlighted(-1);
        return;
      }

      suggestTimerRef.current = setTimeout(() => {
        const controller = new AbortController();
        suggestAbortRef.current = controller;

        fetchAddressSuggestions(value, { caretpos, signal: controller.signal })
          .then((results) => {
            setSuggestions(results);
            setHighlighted(-1);
          })
          .catch(() => {
            // a superseded or failed search just means no list — the address
            // still resolves through the geocode lookup on blur
            if (!controller.signal.aborted) setSuggestions([]);
          });
      }, SUGGEST_DEBOUNCE_MS);
    };

    // Resolves an address and fills the form from the answer. This is the only
    // thing that writes coordinates, whether the address was picked from the
    // list or just typed.
    const runGeocode = (target) => {
      const seq = ++requestSeqRef.current;
      lastLookupRef.current = lookupKey(target);
      setLookupStatus('looking');

      geocodeAddress(target).then(({ status, address: found }) => {
        if (seq !== requestSeqRef.current || !isMountedRef.current) return;

        setLookupStatus(status);

        if (status !== 'resolved') {
          // nothing was rewritten, so the address in the parent is still current.
          // No event: React 17 has already released the synthetic one by now.
          onCheckWoltDeliveryBlur();
          return;
        }

        // the lookup may have corrected the spelling, zip or city
        lastLookupRef.current = lookupKey(found);
        setAddress((current) => ({ ...current, ...found }));
        pendingQuoteRef.current = true;

        if (formTouched) {
          validateStreet(found.street);
          validateCity(found.city);
          validateZipcode(found.zipcode);
        }
      });
    };

    const handleSuggestionSelect = (suggestion) => {
      // A street-name suggestion is not an address yet — it narrows the search
      // so the register starts returning house numbers on that street.
      onAddressEdit?.();

      if (!suggestion.address) {
        setAddress({ ...address, street: suggestion.text, lat: null, long: null });
        setLookupStatus(null);
        runSuggest(suggestion.text, suggestion.caretpos);
        streetInputRef.current?.focus();
        return;
      }

      closeSuggestions();

      // Fill from the register straight away so the form responds to the pick,
      // then send it through the geocode lookup for the coordinates the order
      // needs — which is also what confirms the values.
      const picked = { ...address, ...suggestion.address, lat: null, long: null };
      setAddress(picked);

      if (formTouched) {
        validateStreet(picked.street);
        validateCity(picked.city);
        validateZipcode(picked.zipcode);
      }

      runGeocode(picked);
    };

    const handleStreetKeyDown = (e) => {
      if (!suggestions.length) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlighted((i) => (i + 1) % suggestions.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlighted((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
      } else if (e.key === 'Enter' && highlighted >= 0) {
        e.preventDefault();
        handleSuggestionSelect(suggestions[highlighted]);
      } else if (e.key === 'Escape') {
        closeSuggestions();
      }
    };

    // Runs on blur rather than per keystroke: the lookup wants a whole address,
    // and this is the same moment the quote was already being refreshed.
    const handleAddressBlur = (e) => {
      closeSuggestions();

      // coordinates already stand for exactly these fields — nothing to re-ask,
      // and the quote for them has already run
      if (isResolved) return;

      // a lookup is already in flight — it quotes when it lands, and blurring
      // away from a pick must not get a second, coordinate-less quote in first
      if (lookupStatus === 'looking') return;

      if (!isComplete(address) || lastLookupRef.current === lookupKey(address)) {
        onCheckWoltDeliveryBlur(e);
        return;
      }

      runGeocode(address);
    };

    // Validate street
    const validateStreet = (value) => {
      if (!value || value.trim() === '') {
        setStreetError(true);
        return false;
      }
      setStreetError(false);
      return true;
    };

    // Validate city
    const validateCity = (value) => {
      if (!value || value.trim() === '') {
        setCityError(true);
        return false;
      }
      setCityError(false);
      return true;
    };

    // Validate zipcode
    const validateZipcode = (value) => {
      if (!value || value.trim() === '') {
        setZipcodeError(true);
        return false;
      }
      // Basic zipcode validation - alphanumeric, 3-10 characters
      const zipcodeRegex = /^[a-zA-Z0-9\s-]{3,10}$/;
      const isValid = zipcodeRegex.test(value.trim());
      setZipcodeError(!isValid);
      return isValid;
    };

    // Validate all required fields
    const validateForm = () => {
      setFormTouched(true);
      let isValid = true;

      const isStreetValid = validateStreet(address.street);
      isValid = isValid && isStreetValid;

      const isCityValid = validateCity(address.city);
      isValid = isValid && isCityValid;

      const isZipcodeValid = validateZipcode(address.zipcode);
      isValid = isValid && isZipcodeValid;

      return isValid;
    };

    // Expose the validateForm function to parent component
    useImperativeHandle(ref, () => ({
      validateForm,
    }));

    // Custom handlers with validation. Typing invalidates any coordinates the
    // lookup returned — they belong to the address that was resolved, not to
    // whatever the customer edited it into — and the Wolt quote, for the same
    // reason.
    const handleStreetChange = (value) => {
      onAddressEdit?.();
      setAddress({ ...address, street: value, lat: null, long: null });
      setLookupStatus(null);
      if (formTouched) {
        validateStreet(value);
      }

      runSuggest(value);
    };

    const handleCityChange = (value) => {
      onAddressEdit?.();
      setAddress({ ...address, city: value, lat: null, long: null });
      setLookupStatus(null);
      if (formTouched) {
        validateCity(value);
      }
    };

    const handleZipcodeChange = (value) => {
      onAddressEdit?.();
      setAddress({ ...address, zipcode: value, lat: null, long: null });
      setLookupStatus(null);
      if (formTouched) {
        validateZipcode(value);
      }
    };

    // Error message style
    const errorMsgStyle = 'text-red-500 text-sm mt-1';
    const errorBorderStyle = '!border-red-500';
    const lockedInputStyle = 'bg-[#f5f5f5] text-[#707070] cursor-not-allowed';
    const hintStyle = 'text-xs text-[#707070] mt-1';
    // The address is worth a second look, but none of these block the order —
    // so they read as a warning rather than as a validation error.
    const warningMsgStyle = 'text-xs text-[#b45309] mt-1';

    // Zip and city are the lookup's answer for the address it resolved, so they
    // are locked rather than left editable — a hand-edited zip that no longer
    // matches the coordinates is exactly the mismatch we send to Wolt. Editing the
    // street clears the coordinates, which unlocks both again.
    // Only ever lock a field that actually has a value: some answers come back
    // without a postal code, and locking an empty required field would strand the
    // customer with no way to satisfy validation.
    const isResolved = Number.isFinite(address?.lat) && Number.isFinite(address?.long);
    const zipcodeLocked = isResolved && !!address.zipcode;
    const cityLocked = isResolved && !!address.city;

    // A resolved address needs no note — the locked zip and city already say it
    // came from the lookup. So does an outage: that is our problem, not the
    // customer's, and the order goes through either way. The rest are worth
    // telling them about.
    const lookupHint = (() => {
      if (lookupStatus === 'looking') {
        return {
          text: translations[language]?.address_lookup_checking || 'Checking address...',
          className: hintStyle,
        };
      }

      if (lookupStatus === 'resolved' && !isResolved) {
        return {
          text:
            translations[language]?.address_lookup_no_house_number ||
            'Add the house number so the courier reaches the right door',
          className: warningMsgStyle,
        };
      }

      if (lookupStatus === 'not_found') {
        return {
          text:
            translations[language]?.address_lookup_not_found ||
            'We could not find this address. Please check it.',
          className: warningMsgStyle,
        };
      }

      return null;
    })();

    return (
      <div className="address-information-container space-y-2">
        {/* Street address */}
        <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row">
          <div className="flex-1 mb-2 sm:!mb-0">
            <label className="block text-sm lg:text-base font-bold">
              {translations[language]?.address_street_label || 'Street Address'}*
            </label>
            <p className="text-sm text-[#707070]">
              {translations[language]?.address_street_description ||
                'Include house/unit number if applicable'}
            </p>
          </div>
          <div className="flex-1 relative">
            <input
              ref={streetInputRef}
              type="text"
              placeholder={`${
                translations[language]?.address_street_placeholder || 'Enter your street address'
              }...`}
              value={address.street || ''}
              onChange={(e) => handleStreetChange(e.target.value)}
              onKeyDown={handleStreetKeyDown}
              onBlur={handleAddressBlur}
              // the browser's own address autofill would cover our list
              autoComplete={suggestEnabled ? 'off' : undefined}
              className={`text-sm w-full px-4 py-[14px] border rounded-full appearance-none ${
                streetError ? errorBorderStyle : 'border-[#f4f4f4]'
              }`}
            />
            {suggestions.length > 0 && (
              <ul className="absolute z-30 left-0 right-0 mt-1 max-h-60 overflow-y-auto bg-white border border-[#f4f4f4] rounded-2xl shadow-lg">
                {suggestions.map((suggestion, index) => (
                  <li
                    key={suggestion.key}
                    // mousedown, not click — the input must not blur (and fire a
                    // lookup for the half-typed address) before the pick registers
                    onMouseDown={(e) => {
                      e.preventDefault();
                      handleSuggestionSelect(suggestion);
                    }}
                    onMouseEnter={() => setHighlighted(index)}
                    className={`px-4 py-3 text-sm cursor-pointer ${
                      index === highlighted ? 'bg-[#f5f5f5]' : ''
                    }`}
                  >
                    {suggestion.label}
                  </li>
                ))}
              </ul>
            )}
            {streetError ? (
              <p className={errorMsgStyle}>
                {translations[language]?.address_street_error || 'Please enter your street address'}
              </p>
            ) : (
              lookupHint && <p className={lookupHint.className}>{lookupHint.text}</p>
            )}
          </div>
        </div>

        {/* Zipcode */}
        <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row">
          <div className="flex-1 mb-2 sm:!mb-0">
            <label className="block text-sm lg:text-base font-bold">
              {translations[language]?.address_zipcode_label || 'Zip Code'}*
            </label>
            <p className="text-sm text-[#707070]">
              {translations[language]?.address_zipcode_description || 'Ensures accurate delivery'}
            </p>
          </div>
          <div className="flex-1">
            <input
              type="text"
              placeholder={`${
                translations[language]?.address_zipcode_placeholder || 'Enter your zip code'
              }...`}
              value={address.zipcode || ''}
              onChange={(e) => handleZipcodeChange(e.target.value)}
              onBlur={(e) => !zipcodeLocked && handleAddressBlur(e)}
              readOnly={zipcodeLocked}
              className={`text-sm w-full px-4 py-[14px] border rounded-full appearance-none ${
                zipcodeError ? errorBorderStyle : 'border-[#f4f4f4]'
              } ${zipcodeLocked ? lockedInputStyle : ''}`}
            />
            {zipcodeLocked && (
              <p className={hintStyle}>
                {translations[language]?.address_locked_hint ||
                  'Filled from the selected address — edit the street to change it'}
              </p>
            )}
            {zipcodeError && (
              <p className={errorMsgStyle}>
                {translations[language]?.address_zipcode_error || 'Please enter a valid zip code'}
              </p>
            )}
          </div>
        </div>

        {/* City */}
        <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] flex flex-col sm:flex-row">
          <div className="flex-1 mb-2 sm:!mb-0">
            <label className="block text-sm lg:text-base font-bold">
              {translations[language]?.address_city_label || 'City'}*
            </label>
            <p className="text-sm text-[#707070]">
              {translations[language]?.address_city_description || 'For delivery routing'}
            </p>
          </div>
          <div className="flex-1">
            <input
              type="text"
              placeholder={`${
                translations[language]?.address_city_placeholder || 'Enter your city'
              }...`}
              value={address.city || ''}
              onChange={(e) => handleCityChange(e.target.value)}
              onBlur={(e) => !cityLocked && handleAddressBlur(e)}
              readOnly={cityLocked}
              className={`text-sm w-full px-4 py-[14px] border rounded-full appearance-none ${
                cityError ? errorBorderStyle : 'border-[#f4f4f4]'
              } ${cityLocked ? lockedInputStyle : ''}`}
            />
            {cityLocked && (
              <p className={hintStyle}>
                {translations[language]?.address_locked_hint ||
                  'Filled from the selected address — edit the street to change it'}
              </p>
            )}
            {cityError && (
              <p className={errorMsgStyle}>
                {translations[language]?.address_city_error || 'Please enter your city'}
              </p>
            )}
          </div>
        </div>

        {/* Delivery comment — goes to the Wolt courier, not the kitchen.
          Sits directly under the address because it is part of the
          address: floor, door code, where to leave the food. Shown for
          every delivery order (not gated on the quote having loaded —
          hiding it until a price was fetched made customers miss it).
          Optional: it describes the door, not the map pin, so it neither
          blocks the order nor triggers a re-quote. */}
        <div className="!px-3 !py-4 lg:!px-4 lg:!py-8 border-b border-[#f5f5f5] space-y-2">
          <label className="block text-sm lg:text-base font-bold">
            {translations[language]?.delivery_comment_label}
          </label>
          <textarea
            className="w-full p-4 border border-[#f4f4f4] rounded-xl h-[100px] resize-none text-sm"
            placeholder={`${translations[language]?.delivery_comment_placeholder}...`}
            value={deliveryComment}
            onChange={(e) => onDeliveryCommentChange(e.target.value)}
          ></textarea>
        </div>
      </div>
    );
  },
);

export default AddressInformation;
